import React, { useEffect } from "react";
import {
    Box,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Icon from "src/components/icon";
import styled from "styled-components";
import gitlabself from "src/assets/icons/gitlab-self-hosted";
import Link from "src/components/link";
import { useNavigate } from "react-router-dom";
import request from "src/utils/request";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin-top: 28px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin-top: 32px;
`;

const TextPlaceholder =
    "Please note that Gitlab version 14.7 or later is required to use this feature";

const ContactPlaceholder =
    "In case of any inquiry regarding software permissions, please do not hesitate to ";

const PermissionsGitlabSelfHostedOrg = () => {
    const [hostname, setHostname] = React.useState({ value: undefined });
    const [hostAlreadySet, setHostAlreadySet] = React.useState(false);
    const [token, setToken] = React.useState({ value: undefined });
    const [vpnConfig, setVpnConfig] = React.useState({ value: undefined });
    const [vpnType, setVpnType] = React.useState({ value: undefined });
    const [error, setError] = React.useState(null);
    const [showPassword, setShowPassword] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [isBehindVpn, setIsBehindVpn] = React.useState(false);
    const [vpnConfigExists, setVpnConfigExists] = React.useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const getHostNames = async () => {
            setLoading(true);
            const hostNames = await request(`api/gitlab/self-managed/config`);
            if (hostNames && hostNames.length > 0) {
                // TODO handle multiple hostnames config per org
                setHostname({ value: hostNames[0] });
                setIsBehindVpn(true);
                setHostAlreadySet(true);
                setVpnConfigExists(true);
            }
            setLoading(false);
        };
        getHostNames();
    }, []);

    const checkHostnameValue = () => {
        const re = /^(ftp|http|https):\/\/[^ "]+$/;
        if (!re.test(hostname.value)) {
            const hostnameError = "Please enter a valid url address";
            setHostname({
                ...hostname,
                error: hostnameError,
            });
            return hostnameError;
        }
        return undefined;
    };

    const checkTokenValue = () => {
        if (!token.value || token.value.length < 5) {
            const tokenError = "Please enter a valid personal access token";
            setToken({
                ...token,
                error: tokenError,
            });
            return tokenError;
        }
        return undefined;
    };

    const checkVpnConfigValue = () => {
        if (!vpnConfig.value || vpnConfig.value.length < 5) {
            const err = "Please enter a valid vpn config";
            setVpnConfig((config) => ({
                ...config,
                err,
            }));
            return err;
        }
        return undefined;
    };

    const checkVpnTypeValue = () => {
        if (!vpnConfig.value) {
            const err = "Please select a vpn type";
            setVpnType((config) => ({
                ...config,
                err,
            }));
            return err;
        }
        return undefined;
    };

    const handleSubmit = async (event) => {
        if (event) event.preventDefault();
        const tokenError = checkTokenValue();
        const hostnameError = checkHostnameValue();
        const vpnConfigError = checkVpnConfigValue();
        const vpnTypeError = checkVpnTypeValue();

        if (tokenError || hostnameError) return undefined;
        if (isBehindVpn) {
            if (!vpnConfigError || !vpnTypeError) {
                return undefined;
            }
        }
        try {
            setLoading(true);
            await request(`/api/gitlab/self-managed/connect`, {
                method: "POST",
                body: JSON.stringify({
                    hostname: hostname.value,
                    token: token.value,
                    isBehindVpn,
                    vpnConfig: vpnConfig.value,
                    centrallyManaged: true,
                }),
            });
        } catch (e) {
            setLoading(false);
            if (e.status === 400) {
                return setError(e.message);
            }
            return navigate("/integrations/failure/gitlab");
        }
        setLoading(true);
        return navigate("/integrations/success/gitlab");
    };

    const changeHostnameValue = (event) => {
        setHostname({
            ...hostname,
            error: undefined,
            value: event.target.value,
        });
    };

    const changeTokenValue = (event) => {
        setToken({
            ...token,
            error: undefined,
            value: event.target.value,
        });
    };

    const changeVpnConfigValue = (event) => {
        setVpnConfig((config) => ({
            ...config,
            error: undefined,
            value: event.target.value,
        }));
    };

    const changeVpnTypeValue = (event) => {
        setVpnType((config) => ({
            ...config,
            error: undefined,
            value: event.target.value,
        }));
    };

    return (
        <>
            <Helmet>
                <title>Adadot - Gitlab Self Managed Permissions</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <form onSubmit={handleSubmit}>
                        <H1>Self Hosted GitLab Repository</H1>
                        <H2>Please add your url and access token below</H2>
                        <PermissionsIcon svg={gitlabself} width="100px" height="127" />
                        <P>{TextPlaceholder}</P>
                        <Box
                            sx={{
                                width: 500,
                                maxWidth: "100%",
                                paddingTop: "2rem",
                            }}
                        >
                            <TextField
                                error={!!hostname.error}
                                value={hostname.value || ""}
                                helperText={
                                    hostAlreadySet
                                        ? "Host url is set by your organization "
                                        : hostname.error
                                }
                                variant="outlined"
                                onChange={(event) => changeHostnameValue(event)}
                                onBlur={() => checkHostnameValue()}
                                onFocus={() => setHostname({ ...hostname, error: undefined })}
                                fullWidth
                                label="Enter url"
                                id="url-input"
                                disabled={hostAlreadySet}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: 500,
                                maxWidth: "100%",
                                paddingTop: "2rem",
                            }}
                        >
                            <TextField
                                error={!!token.error}
                                value={token.value}
                                helperText={token.error}
                                variant="outlined"
                                autoComplete="off"
                                type={showPassword ? "text" : "password"}
                                onChange={(event) => changeTokenValue(event)}
                                onBlur={() => checkTokenValue()}
                                onFocus={() => setToken({ ...token, error: undefined })}
                                fullWidth
                                label="Enter Access Token"
                                id="access-token-input"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        {isBehindVpn && !vpnConfigExists && (
                            <>
                                <Box
                                    sx={{
                                        width: 500,
                                        maxWidth: "100%",
                                        paddingTop: "2rem",
                                    }}
                                >
                                    <TextField
                                        multiline
                                        error={!!vpnConfig.error}
                                        value={vpnConfig.value}
                                        helperText={vpnConfig.error}
                                        variant="outlined"
                                        autoComplete="off"
                                        onBlur={() => checkVpnConfigValue()}
                                        onFocus={() =>
                                            setVpnConfig((config) => ({
                                                ...config,
                                                error: undefined,
                                            }))
                                        }
                                        onChange={(event) => changeVpnConfigValue(event)}
                                        fullWidth
                                        label="Enter your OpenVPN Configuration"
                                        id="vpn-config-input"
                                        rows={8}
                                        maxRows={20}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        width: 500,
                                        maxWidth: "100%",
                                        paddingTop: "2rem",
                                    }}
                                >
                                    <FormControl>
                                        <FormLabel id="demo-controlled-radio-buttons-group">
                                            VPN Configuration type
                                        </FormLabel>
                                        <RadioGroup
                                            aria-labelledby="demo-controlled-radio-buttons-group"
                                            name="controlled-radio-buttons-group"
                                            row
                                            value={vpnType.value}
                                            onBlur={() => checkVpnTypeValue()}
                                            onFocus={() =>
                                                setVpnType((config) => ({
                                                    ...config,
                                                    error: undefined,
                                                }))
                                            }
                                            onChange={changeVpnTypeValue}
                                        >
                                            <FormControlLabel
                                                value="openvpn"
                                                control={<Radio />}
                                                label="OpenVPN"
                                            />
                                            <FormControlLabel
                                                value="ikev2"
                                                control={<Radio />}
                                                label="IKEv2"
                                            />
                                            <FormControlLabel
                                                value="else"
                                                control={<Radio />}
                                                label="Something Else"
                                            />
                                        </RadioGroup>
                                        <FormHelperText error={vpnType.error}>
                                            {vpnType.error}
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        <FormGroup style={{ paddingTop: "28px" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        disabled={hostAlreadySet}
                                        size="small"
                                        checked={isBehindVpn}
                                        onChange={() => setIsBehindVpn(!isBehindVpn)}
                                    />
                                }
                                label="Is behind VPN"
                            />
                        </FormGroup>
                        <P>
                            {ContactPlaceholder}
                            <Link to="/upgrade/inquiry">contact us</Link>
                        </P>
                        <Stack mt={4} direction="row" spacing={2}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                type="submit"
                                onClick={handleSubmit}
                                style={{ width: "102px" }}
                                loading={loading}
                                disabled={vpnType.value && vpnType.value !== "openvpn"}
                            >
                                Continue
                            </LoadingButton>
                            <LoadingButton
                                variant="contained"
                                color={
                                    vpnType.value && vpnType.value !== "openvpn"
                                        ? "primary"
                                        : "secondary"
                                }
                                type="submit"
                                loading={loading}
                                href="mailto:info@adadot.com"
                            >
                                Contact us to set it up
                            </LoadingButton>
                        </Stack>
                        <FormHelperText error>{error}</FormHelperText>
                    </form>
                </Stack>
            </Grid>
        </>
    );
};

export default PermissionsGitlabSelfHostedOrg;
